import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Eierautomat kaufen“ (/eierautomat).
 *
 * Ziel-Keyword: „eierautomat“ (~2.650 Suchen/Monat) mit Kaufabsicht. Die Seite
 * bedient den transaktionalen Intent (Kaufen/Mieten, Modelle, Preise, CTA zum
 * Shop) und verlinkt bewusst auf den bestehenden Ratgeber-Blogartikel
 * „Eier aus dem Automaten“, der den informationalen Intent abdeckt – so
 * ergaenzen sich beide Ebenen statt zu kannibalisieren.
 *
 * SEO-Muster analog rechner-/pdp-Seiten: Title, Meta-Description, Open Graph,
 * Canonical und JSON-LD (Product + FAQPage) serverseitig im ngOnInit gesetzt,
 * damit Crawler alles im SSR-/Prerender-HTML sehen.
 */
@Component({
  selector: 'app-eierautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './eierautomat-page.component.html',
  styleUrls: ['./eierautomat-page.component.scss', '../cluster-landing.shared.scss'],
})
export class EierautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  /** Slug des passenden Ratgeber-Artikels (informationaler Zubringer). */
  readonly blogEier = 'eier-aus-dem-automaten';
  /**
   * Öffentlicher Live-Link der Eier-Box Kempershof (Echtzeit-Verfügbarkeit).
   * Hinweis: NICHT die app.marktbox.de-Variante nutzen – die ist login-geschützt
   * und würde eingebettet nur ein Login-Formular zeigen.
   */
  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/kempershof/U-MB-00321'
  );

  /** Nur belegte Fakten aus dem Repo (siehe /shop-Lead): keine erfundenen Specs. */
  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@eier.faq1.q:Was kostet ein Eierautomat?`,
      a: $localize`:@@eier.faq1.a:Die marktbox als Eierautomat gibt es ab 6.800 € zum Kauf oder ab 300 € pro Monat zur Miete – je nach Modell (24 oder 39 Fächer) und Zubehör.`,
    },
    {
      q: $localize`:@@eier.faq2.q:Eierautomat kaufen oder mieten?`,
      a: $localize`:@@eier.faq2.a:Beides ist möglich. Kaufen lohnt sich bei dauerhaftem Betrieb, Mieten senkt die Einstiegshürde. Eine ausführliche Entscheidungshilfe findest du in unserem Ratgeber.`,
    },
    {
      q: $localize`:@@eier.faq3.q:Lohnt sich ein Eierautomat für die Direktvermarktung?`,
      a: $localize`:@@eier.faq3.a:Ein Eierautomat verkauft rund um die Uhr, bargeldlos und ohne zusätzliches Personal – ideal, um frische Eier regional und ohne Ladenöffnungszeiten direkt zu vermarkten.`,
    },
    {
      q: $localize`:@@eier.faq4.q:Wo kann ich einen Eierautomaten aufstellen?`,
      a: $localize`:@@eier.faq4.a:Die marktbox lässt sich flexibel aufstellen – direkt ab Hof, am Feldrand, vor dem Laden oder an gut sichtbaren Standorten mit Laufkundschaft. Wichtig sind Sichtbarkeit und einfache Erreichbarkeit.`,
    },
  ];

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@eier.metaTitle:Eierautomat kaufen & mieten | marktbox`;
    const description =
      $localize`:@@eier.metaDesc:Eierautomat kaufen oder mieten: frische Eier 24/7 verkaufen – bargeldlos, ohne Personal. marktbox ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content:
        $localize`:@@eier.metaKeywords:Eierautomat, Eierautomat kaufen, Eierautomat mieten, Eier verkaufen Automat, Verkaufsautomat Eier, Direktvermarktung, marktbox`,
    });

    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('eierautomat-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Eierautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('eierautomat-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  ngAfterViewInit(): void {
    // Scroll-Reveal-Animationen (browser-only, respektiert prefers-reduced-motion;
    // ohne JS bleiben die .reveal-Elemente sichtbar → SSR/Crawler unberührt).
    this.observers = initFeatureFx(this.host.nativeElement);

    // Sicherheitsnetz: Feuern die IntersectionObserver-Callbacks nicht (bfcache-
    // Restore, gedrosselte/headless Tabs), blieben .reveal-Elemente auf opacity:0.
    // Daher nach kurzer Zeit alle noch verborgenen Elemente sicher einblenden.
    // In echten Browsern hat IO längst reveal-in gesetzt → No-op.
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  /** In-Page-Anker – plain href="#id" scheitert an <base href="/">. */
  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/eierautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
